import React, { useState } from 'react';
import { Phone, MessageCircle, ShieldCheck, UserCheck, Leaf, Users, Star, CheckCircle } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { mockData } from '../mock';
import { ShopBanner } from '../components/ShopBanner';
import { SurveyDialog } from '../components/SurveyDialog';
import { CalendarCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  'home': LucideIcons.Home,
  'sparkles': LucideIcons.Sparkles,
  'square': LucideIcons.Square,
  'droplet': LucideIcons.Droplet,
  'building': LucideIcons.Building2,
  'key': LucideIcons.Key,
  'hammer': LucideIcons.Hammer,
  'flower': LucideIcons.Flower2,
  'calendar': LucideIcons.Calendar,
  'shield-check': ShieldCheck,
  'user-check': UserCheck,
  'leaf': Leaf,
  'users': Users
};

export const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    bedrooms: '',
    bathrooms: '',
    cleaningType: '',
    kitchenAppliances: [],
    message: '',
    images: []
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (applianceId) => {
    setFormData(prev => ({
      ...prev,
      kitchenAppliances: prev.kitchenAppliances.includes(applianceId)
        ? prev.kitchenAppliances.filter(id => id !== applianceId)
        : [...prev.kitchenAppliances, applianceId]
    }));
  };

  const [submitting, setSubmitting] = useState(false);
  const [surveyOpen, setSurveyOpen] = useState(false);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const valid = [];
    for (const f of files) {
      if (!f.type.startsWith('image/')) { toast.error(`${f.name} is not an image`); continue; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} is larger than 10 MB`); continue; }
      valid.push(f);
    }
    const merged = [...formData.images, ...valid].slice(0, 5);
    if (formData.images.length + valid.length > 5) toast.error('You can attach up to 5 pictures');
    setFormData(prev => ({ ...prev, images: merged }));
    e.target.value = '';
  };

  const removeImage = (idx) => {
    setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('email', formData.email);
      data.append('phone', formData.phone);
      data.append('address', formData.address);
      data.append('bedrooms', parseInt(formData.bedrooms));
      data.append('bathrooms', parseInt(formData.bathrooms));
      data.append('cleaningType', formData.cleaningType);
      formData.kitchenAppliances.forEach(a => data.append('kitchenAppliances', a));
      data.append('message', formData.message);
      formData.images.forEach(img => data.append('images', img, img.name));

      const response = await axios.post(`${API}/quotes/submit`, data);
      
      if (response.data) {
        toast.success(`Quote request sent successfully! We'll contact you soon. Reference: ${response.data.id.substring(0, 8).toUpperCase()}`);
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          bedrooms: '',
          bathrooms: '',
          cleaningType: '',
          kitchenAppliances: [],
          message: '',
          images: []
        });
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to submit quote. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent('Hi! I would like to get a quote for cleaning services.');
    window.open(`https://wa.me/${mockData.company.whatsapp}?text=${message}`, '_blank', 'noopener');
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-4 bg-gradient-to-b from-pink-50 to-white">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-[1.05]">
                Professional Cleaning Services in <span className="text-pink-600">Peterborough</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Reliable, insured, and DBS-checked home cleaning services. Let's make your home shine!
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  size="lg"
                  className="bg-pink-500 hover:bg-pink-600 text-white"
                  onClick={() => window.open('tel:+447842860575', '_self')}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </Button>
                <Button
                  size="lg"
                  className="bg-gray-900 hover:bg-gray-800 text-white"
                  onClick={() => setSurveyOpen(true)}
                  data-testid="hero-book-survey"
                >
                  <CalendarCheck className="w-5 h-5 mr-2" />
                  Book Free Home Survey
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-green-500 text-green-600 hover:bg-green-50"
                  onClick={handleWhatsApp}
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Get Free Quote
                </Button>
              </div>
              <p className="text-sm text-gray-500 mt-4">Free, no-obligation home visit — we look around, listen, and give you an accurate price.</p>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={mockData.company.teamPhoto}
                  alt="Clean Home Peterborough Team"
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 border-4 border-pink-100">
                <div className="flex items-center space-x-2">
                  <Star className="w-6 h-6 text-yellow-500 fill-yellow-500" />
                  <div>
                    <p className="font-bold text-gray-900">5.0 Rating</p>
                    <p className="text-sm text-gray-600">100+ Happy Clients</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {mockData.trustBadges.map((badge) => {
              const IconComponent = iconMap[badge.icon];
              return (
                <div key={badge.id} className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-pink-100 rounded-full mb-3">
                    <IconComponent className="w-8 h-8 text-pink-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1">{badge.title}</h3>
                  <p className="text-sm text-gray-600">{badge.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">About Clean Home Peterborough</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Clean Home Peterborough is a team of experienced, trustworthy cleaners who care about making your home spotless. We are fully insured, DBS-checked, and use eco-friendly cleaning products to ensure the safety of your family and pets.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              With years of experience serving Peterborough and surrounding areas, we pride ourselves on delivering exceptional cleaning services that exceed expectations. Your satisfaction is our priority.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-xl text-gray-600">Comprehensive cleaning solutions for every need</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockData.services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-2 hover:border-pink-200">
                  <CardHeader>
                    <div className="w-14 h-14 bg-gradient-to-br from-pink-100 to-pink-50 rounded-xl flex items-center justify-center mb-4">
                      <IconComponent className="w-7 h-7 text-pink-600" />
                    </div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">{service.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="text-center mt-8">
            <p className="text-lg text-gray-600 font-medium">...and much more!</p>
          </div>
        </div>
      </section>

      {/* Shop promo banner */}
      <ShopBanner />

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-xl text-gray-600">Don't just take our word for it</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {mockData.testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-gradient-to-br from-white to-pink-50 border-2 border-pink-100">
                <CardHeader>
                  <div className="flex items-center space-x-1 mb-2">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>
                  <CardTitle className="text-lg">{testimonial.name}</CardTitle>
                  <CardDescription>{testimonial.location}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 italic">"{testimonial.text}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">Everything you need to know</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {mockData.faqs.map((faq) => (
                <AccordionItem key={faq.id} value={`item-${faq.id}`} className="bg-white rounded-lg border-2 border-gray-200 px-6">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-pink-600">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Get in Touch</h2>
            <p className="text-xl text-gray-600">Request a free quote or ask us anything</p>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div>
                <Card>
                  <CardHeader>
                    <CardTitle>Request a Quote</CardTitle>
                    <CardDescription>Fill out the form and we'll get back to you soon</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Input
                            type="text"
                            name="name"
                            placeholder="Your Name *"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div>
                          <Input
                            type="email"
                            name="email"
                            placeholder="Email Address *"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Input
                            type="tel"
                            name="phone"
                            placeholder="Phone Number *"
                            value={formData.phone}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div>
                          <Input
                            type="text"
                            name="address"
                            placeholder="Property Address *"
                            value={formData.address}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Input
                            type="number"
                            name="bedrooms"
                            placeholder="How many bedrooms? *"
                            value={formData.bedrooms}
                            onChange={handleInputChange}
                            min="1"
                            required
                          />
                        </div>
                        <div>
                          <Input
                            type="number"
                            name="bathrooms"
                            placeholder="How many bathrooms? *"
                            value={formData.bathrooms}
                            onChange={handleInputChange}
                            min="1"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <select
                          name="cleaningType"
                          value={formData.cleaningType}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                        >
                          <option value="">What kind of cleaning is required? *</option>
                          {mockData.cleaningTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Kitchen Appliances (optional):</label>
                        <div className="space-y-2">
                          {mockData.kitchenAppliances.map((appliance) => (
                            <label key={appliance.id} className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.kitchenAppliances.includes(appliance.id)}
                                onChange={() => handleCheckboxChange(appliance.id)}
                                className="w-4 h-4 text-pink-500 border-gray-300 rounded focus:ring-pink-500"
                              />
                              <span className="text-sm text-gray-700">{appliance.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div>
                        <Textarea
                          name="message"
                          placeholder="Additional details or questions..."
                          value={formData.message}
                          onChange={handleInputChange}
                          rows={3}
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Attach Pictures (optional, up to 5, max 10 MB each):</label>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          data-testid="quote-images-input"
                          onChange={handleImageUpload}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 text-sm"
                        />
                        {formData.images.length > 0 && (
                          <div className="flex flex-wrap gap-2" data-testid="quote-images-preview">
                            {formData.images.map((img, idx) => (
                              <div key={idx} className="relative w-16 h-16 rounded-md overflow-hidden border border-gray-200">
                                <img src={URL.createObjectURL(img)} alt={img.name} className="w-full h-full object-cover" />
                                <button
                                  type="button"
                                  onClick={() => removeImage(idx)}
                                  data-testid={`remove-image-${idx}`}
                                  className="absolute top-0 right-0 bg-black/60 text-white text-xs w-5 h-5 flex items-center justify-center"
                                  aria-label="Remove picture"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <Button type="submit" disabled={submitting} data-testid="quote-submit-button" className="w-full bg-pink-500 hover:bg-pink-600 text-white">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {submitting ? 'Sending...' : 'Send Request'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>

              {/* Contact Info */}
              <div className="space-y-6">
                <Card className="border-2 border-gray-900 bg-gray-900 text-white">
                  <CardContent className="pt-6 flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-pink-500 flex items-center justify-center shrink-0"><CalendarCheck className="w-6 h-6" /></div>
                    <div className="flex-1">
                      <p className="font-bold text-lg leading-tight">Prefer a visit first?</p>
                      <p className="text-sm text-gray-300">Book a free home survey — we'll come round, look at the property and discuss what you need.</p>
                    </div>
                    <Button onClick={() => setSurveyOpen(true)} className="bg-pink-500 hover:bg-pink-400 text-white rounded-full shrink-0" data-testid="contact-book-survey">Book survey</Button>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-pink-500 to-pink-600 text-white border-0">
                  <CardHeader>
                    <CardTitle className="text-white">Contact Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-start space-x-4">
                      <Phone className="w-6 h-6 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-semibold">Phone</p>
                        <a href={`tel:${mockData.company.phone}`} className="hover:underline">
                          {mockData.company.phoneDisplay}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <MessageCircle className="w-6 h-6 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-semibold">WhatsApp</p>
                        <button onClick={handleWhatsApp} className="hover:underline text-left">
                          Message us on WhatsApp
                        </button>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <LucideIcons.Mail className="w-6 h-6 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-semibold">Email</p>
                        <a href={`mailto:${mockData.company.email}`} className="hover:underline break-all">
                          {mockData.company.email}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <LucideIcons.MapPin className="w-6 h-6 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-semibold">Location</p>
                        <p>{mockData.company.location}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-500 to-pink-500 text-white border-0">
                  <CardContent className="pt-6">
                    <div className="flex items-center space-x-4">
                      <LucideIcons.Instagram className="w-12 h-12" />
                      <div>
                        <p className="font-semibold mb-1">Follow us on Instagram</p>
                        <a
                          href={mockData.company.instagramUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-lg font-bold hover:underline"
                        >
                          {mockData.company.instagram}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
      <SurveyDialog open={surveyOpen} onOpenChange={setSurveyOpen} />
    </div>
  );
};
